import React, { useMemo } from 'react';
import { Box, Typography, Card, CardContent, Chip, Stack, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { getDaysUntilQuarterEnd, getQuarterEndDate } from 'services/Utilities/submissionUtils';

import { getStatusConfig, getStatusLabel } from 'enums/CertificationStatusEnum';
import { SubmissionDetailStatusEnumValue } from 'enums/SubmissionDetailStatusEnum';

const SubmissionCard = ({ submission }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const theme = useTheme();
  const statusConfig = getStatusConfig(submission.status) || {};
  const StatusIcon = statusConfig?.icon;
  const submissionDate = submission.dateIssued ? new Date(submission.dateIssued) : null;
  const statusLabel = getStatusLabel(submission.status, i18n.language) || t('submissions.status.unknown');

  const hasFeedbackAvailable = useMemo(() => {
    if (!Array.isArray(submission?.submissionDetails)) {
      return false;
    }

    return submission.submissionDetails.some(detail => {
      const detailStatus = detail?.status;
      return (
        detailStatus === SubmissionDetailStatusEnumValue.ImprovementRequired ||
        detailStatus === SubmissionDetailStatusEnumValue.BrpBestPractice
      );
    });
  }, [submission]);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language === 'fr' ? 'fr-CA' : 'en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }),
    [i18n.language],
  );

  const quarterEndDate = getQuarterEndDate(submission);
  const daysUntilQuarterEnd = getDaysUntilQuarterEnd(submission);

  let quarterPrimaryLabel = null;
  let quarterSecondaryLabel = null;

  if (quarterEndDate) {
    const formattedDate = dateFormatter.format(quarterEndDate);
    if (daysUntilQuarterEnd === null) {
      quarterPrimaryLabel = t('submissions.quarterEndsOn', { date: formattedDate });
    } else if (daysUntilQuarterEnd > 0) {
      quarterPrimaryLabel = t('submissions.quarterEndsOn', { date: formattedDate });
      quarterSecondaryLabel = t('submissions.quarterDaysLeft', { count: daysUntilQuarterEnd });
    } else if (daysUntilQuarterEnd === 0) {
      quarterPrimaryLabel = t('submissions.quarterEndsToday');
      quarterSecondaryLabel = t('submissions.quarterEndsOn', { date: formattedDate });
    } else {
      quarterPrimaryLabel = t('submissions.quarterEndedOn', { date: formattedDate });
    }
  }

  const quarterSecondaryColor = daysUntilQuarterEnd > 0 ? theme.palette.primary.main : theme.palette.text.secondary;
  const hasScore = submission.score !== null && submission.score !== undefined && submission.score !== 0;

  const handleCardClick = () => {
    history.push(`/dealer/certification/submissions/${submission.id}/step/1`);
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        boxShadow: theme.shadows[4],
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          borderColor: theme.palette.secondary.main,
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[8],
        },
      }}>
      <CardContent sx={{ flex: 1, display: 'flex' }}>
        <Stack spacing={2} sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {submission.name || t('submissions.untitled')}
              </Typography>
              {submissionDate && <Typography variant="caption">{dateFormatter.format(submissionDate)}</Typography>}
            </Box>
            <Stack direction="column" spacing={1} sx={{ flexShrink: 0, alignItems: 'flex-end' }}>
              <Chip
                icon={StatusIcon ? <StatusIcon sx={{ fontSize: 18 }} /> : null}
                label={statusLabel}
                size="small"
                sx={{
                  bgcolor: statusConfig.bgColor || 'rgba(153,153,153,0.1)',
                  color: statusConfig.color || '#999',
                  fontWeight: 600,
                  border: '1px solid',
                  borderColor: statusConfig.color || '#999',
                  '& .MuiChip-icon': {
                    color: statusConfig.color || '#999',
                  },
                }}
              />
              {hasFeedbackAvailable ? (
                <Chip
                  label={t('submissions.feedbackAvailable')}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(2,136,209,0.08)',
                    color: theme.palette.info.main,
                    fontWeight: 600,
                    border: `1px dashed ${theme.palette.info.main}`,
                  }}
                />
              ) : null}
            </Stack>
          </Box>

          {submission.quarter?.internalName && (
            <Box>
              <Typography
                variant="h3"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  display: 'block',
                }}>
                {submission.quarter.internalName}
              </Typography>

              {quarterPrimaryLabel && (
                <Typography variant="caption" sx={{ mt: 0.5 }}>
                  {quarterPrimaryLabel}
                </Typography>
              )}

              {quarterSecondaryLabel && (
                <Typography variant="h3" sx={{ color: quarterSecondaryColor, fontWeight: 700, mt: 0.25 }}>
                  {quarterSecondaryLabel}
                </Typography>
              )}
            </Box>
          )}

          {hasScore ? (
            <Box>
              <Typography variant="caption" color="text.primary" sx={{ display: 'block', mb: 0.5 }}>
                {t('submissions.score')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {t('submissions.scorePoints', { count: submission.score })}
              </Typography>
            </Box>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SubmissionCard;
